// Shared IPC calculation helpers. Mirrors the arquiler.com convention:
// the months consumed in a rental cycle are [fechaDesde, fechaHasta), i.e.
// the start month inclusive, the next-adjustment month exclusive. When the
// last month is not yet published, it is projected with the average of the
// published months in the same period and flagged as "aproximado".

// Parse "YYYY-MM-DD" as a LOCAL date. `new Date("YYYY-MM-DD")` is parsed as
// UTC midnight, which in UTC-3 lands on the previous day at 21:00 and shifts
// the displayed month one back.
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, d || 1);
};

export const mesesEnPeriodo = (fechaDesde, fechaHasta) => {
  const arr = [];
  const cursor = new Date(fechaDesde.getFullYear(), fechaDesde.getMonth(), 1);
  const fin = new Date(fechaHasta.getFullYear(), fechaHasta.getMonth(), 1);
  while (cursor < fin) {
    arr.push({ mes: cursor.getMonth() + 1, anio: cursor.getFullYear() });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return arr;
};

// historial: [{ mes, anio, valor }]. Returns null if zero months in range or
// zero published. When some months are missing, projects each with the
// average of the published months (arquiler.com convention).
export const calcularFactorIPC = (historial, fechaDesde, fechaHasta) => {
  const meses = mesesEnPeriodo(fechaDesde, fechaHasta);
  if (meses.length === 0) return null;

  const lookup = new Map();
  for (const e of historial || []) {
    const v = Number(e.valor);
    if (Number.isFinite(v)) lookup.set(`${e.anio}-${e.mes}`, v);
  }

  let sumaPublicada = 0;
  let publicados = 0;
  const faltantes = [];
  for (const { mes, anio } of meses) {
    if (lookup.has(`${anio}-${mes}`)) {
      sumaPublicada += lookup.get(`${anio}-${mes}`);
      publicados++;
    } else {
      faltantes.push({ mes, anio });
    }
  }

  if (publicados === 0) return null;

  const promedio = sumaPublicada / publicados;
  const sumaProyectada = promedio * faltantes.length;
  const suma = sumaPublicada + sumaProyectada;

  return {
    factor: 1 + suma / 100,
    porcentaje: suma.toFixed(2),
    porcentajePublicado: sumaPublicada.toFixed(2),
    promedio: promedio.toFixed(2),
    publicados,
    totalMeses: meses.length,
    meses,
    faltantes,
    aproximado: faltantes.length > 0,
  };
};

// ICL is an absolute index value: ratio between two month-end values.
export const calcularFactorICL = (historial, fechaDesde, fechaHasta) => {
  const dMes = fechaDesde.getMonth() + 1, dAnio = fechaDesde.getFullYear();
  const hMes = fechaHasta.getMonth() + 1, hAnio = fechaHasta.getFullYear();
  const desdeIdx = (historial || []).find((i) => i.mes === dMes && i.anio === dAnio);
  const hastaIdx = (historial || []).find((i) => i.mes === hMes && i.anio === hAnio);
  if (!desdeIdx || !hastaIdx) return null;
  const dV = Number(desdeIdx.valor);
  const hV = Number(hastaIdx.valor);
  if (!dV || !hV) return null;
  const factor = hV / dV;
  return {
    factor,
    porcentaje: ((factor - 1) * 100).toFixed(2),
    aproximado: false,
    publicados: 2,
    totalMeses: 2,
  };
};

// INDEC publishes IPC around the 12th-15th of the next month. Day 12 is a
// reasonable rough estimate without baking in the official calendar.
export const fechaPublicacionEstimada = (mes, anio) => {
  let m = mes + 1, a = anio;
  if (m > 12) { m = 1; a++; }
  return `${a}-${String(m).padStart(2, "0")}-12`;
};

const MESES_LABEL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export const nombreMes = (mes) => MESES_LABEL[(mes - 1) % 12] || "";
